import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { categoryColor, groupColor } from '../../lib/entityColors';
import { fetchExploreCategories } from '../../lib/exploreApi';
import type {
  ExploreCategoriesResponse, ExploreCategoryGroup, ExploreSearchResult,
} from '../../lib/exploreApi';
import QueryInput from '../../components/QueryInput';
import './exploreSearch.css';

// Legacy intel categories (CVE / Techniques / Actors / Families) live outside
// the IOC registry — they have their own Neo4j node types and richer columns.
const INTEL_CATEGORIES = [
  { value: 'cve',       label: 'CVEs' },
  { value: 'technique', label: 'TTPs' },
  { value: 'actor',     label: 'Actors' },
  { value: 'family',    label: 'Families' },
  { value: 'country',   label: 'Countries' },
] as const;

interface ExploreHeaderProps {
  query: string;
  onQueryChange: (q: string) => void;
  onSubmit: (q: string) => void;
  onPickEntity: (entity: ExploreSearchResult) => void;
  category: string;
  onCategoryChange: (cat: string) => void;
}

/** Sum counts of all sub-categories in a group. */
function groupTotal(grp: ExploreCategoryGroup): number {
  return grp.categories.reduce((sum, c) => sum + c.count, 0);
}

/** Resolve which group is currently active based on the selected category key. */
function activeGroup(
  category: string,
  groups: ExploreCategoryGroup[],
): ExploreCategoryGroup | null {
  if (!category) return null;
  if (category.startsWith('group:')) {
    const key = category.slice('group:'.length);
    return groups.find(g => g.key === key) ?? null;
  }
  return groups.find(g => g.categories.some(c => c.key === category)) ?? null;
}

export default function ExploreHeader({
  query, onQueryChange, onSubmit, onPickEntity,
  category, onCategoryChange,
}: ExploreHeaderProps) {
  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });

  const groups = catsData?.groups ?? [];

  const currentGroup = useMemo(
    () => activeGroup(category, groups),
    [category, groups],
  );

  const currentSubKey = !category.startsWith('group:') ? category : '';

  return (
    <div className="explore-header">
      <div className="explore-search-row">
        <QueryInput
          mode="search"
          size="lg"
          value={query}
          onChange={onQueryChange}
          onSubmit={onSubmit}
          onPickEntity={onPickEntity}
          autoFocus
          ariaLabel="Search entities"
          placeholder="Search actors, families, IOCs, CVEs, techniques… try actor:apt country:RU"
          footerAction={query.trim() ? {
            label: `See all results for “${query.trim()}” →`,
            onSelect: () => onSubmit(query),
          } : undefined}
        />
      </div>

      <div className="explore-tabs">
        <button
          className={`explore-tab${category === '' ? ' explore-tab--active' : ''}`}
          onClick={() => onCategoryChange('')}
        >
          All
        </button>

        {groups.length > 0 && (
          <span className="explore-tab-group-label">Indicators</span>
        )}
        {groups.map((grp) => {
          const groupKey = `group:${grp.key}`;
          const isActive = category === groupKey || currentGroup?.key === grp.key;
          const total = groupTotal(grp);
          return (
            <button
              key={grp.key}
              className={`explore-tab${isActive ? ' explore-tab--active' : ''}`}
              onClick={() => onCategoryChange(groupKey)}
              style={isActive ? { '--tab-color': groupColor(grp.key) } as React.CSSProperties : undefined}
              title={`${grp.label}: ${total.toLocaleString()} entries`}
            >
              {grp.label}
            </button>
          );
        })}

        {groups.length > 0 && <span className="explore-tab-group-sep" />}

        <span className="explore-tab-group-label">Intel</span>
        {INTEL_CATEGORIES.map(({ value, label }) => (
          <button
            key={value}
            className={`explore-tab${category === value ? ' explore-tab--active' : ''}`}
            onClick={() => onCategoryChange(value)}
            style={category === value ? { '--tab-color': categoryColor(value) } as React.CSSProperties : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {currentGroup && currentGroup.categories.length > 1 && (
        <div className="explore-subtype-chips">
          <button
            className={`explore-chip${category === `group:${currentGroup.key}` ? ' explore-chip--active' : ''}`}
            onClick={() => onCategoryChange(`group:${currentGroup.key}`)}
            style={{ '--tab-color': groupColor(currentGroup.key) } as React.CSSProperties}
          >
            All {currentGroup.label}
          </button>
          {currentGroup.categories.map((c) => {
            const isActive = currentSubKey === c.key;
            return (
              <button
                key={c.key}
                className={`explore-chip${isActive ? ' explore-chip--active' : ''}`}
                onClick={() => onCategoryChange(c.key)}
                style={{ '--tab-color': categoryColor(c.key) } as React.CSSProperties}
                title={`${c.label}: ${c.count.toLocaleString()} entries`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
